import React, { Component } from 'react';
import { findDOMNode } from 'react-dom';
import { connect } from 'react-redux';
import { selectSchool } from '../../actions';

import { format } from '../../modules/helpers';
import * as d3 from 'd3';
import Tooltip from '../tooltip';
import drawKey from './drawKey';
import getYDomain from './getYDomain';
import getCustomAxis from './getCustomAxis';
import addNodes from './addNodes';
import addXAxisLabels from './addXAxisLabels';
import enrolmentPercentLabel from './enrolmentPercentLabel';
import actualAndProjectedGrowth from './actualAndProjectedGrowth';
import clusterAggregate from './clusterAggregate';
import getSchoolColor from './getSchoolColor';
import isSmallProjectedNode, { isSchoolSmall } from './isSmallProjectedNode';

let schoolWithLineNodes;

function makeProjectionsLineGraph(activeSchool, activeCluster) {
  const SVGwidth = 1000;
  const SVGheight = 500;
  const axisPadding = 40;
  const domainPadding = 40;
  let tooltip;
  let svg;

  const data = activeCluster.schools.map(d => d).filter(d => {
    return Array.isArray(d.projections_percentage);
  });

  // Add the cluster aggregate as "another school"
  const cluster = clusterAggregate(data, activeCluster);

  // The activeSchool is the one that's locally active - ie, that'll have
  // node points with percentages above shown on it's line
  // We set defaults here, but you can change it with the prev/next slider.
  if (activeSchool) {
    schoolWithLineNodes = activeSchool;
  } else {
    schoolWithLineNodes = cluster;
  }
  data.push(cluster);

  const margin = {
    top: 50,
    right: domainPadding,
    bottom: 40 + axisPadding,
    left: 10 + axisPadding + domainPadding,
  };
  const width = SVGwidth - margin.left - margin.right;
  const height = SVGheight - margin.bottom - margin.top;
  const elm = document.createElement('div');
  const projectionStartDate = new Date(); // This should be projections start date
  let isLimited = true;
  const isThisSchoolSmall = isSchoolSmall(schoolWithLineNodes); 

  function setupToolTip() {
    const content = `<div class="tooltip-content-wrapper">
        <div class="tooltip-content">
        <div>
          <p class="tooltip-title"></p>
          <p class="tooltip-baseline"></p>
          <p class="tooltip-projection"></p>
        </div>
        </div>
      </div>`;

    tooltip = new Tooltip(content);
    elm.appendChild(tooltip.elm);
  }
  let hideTimeout;
  const handleLineNodeClick = function(d, i) {
    d3.event.stopPropagation();

    let yearsLater = (i - 1) * 5;
    tooltip.show();
    const inc = schoolWithLineNodes.projections_percentage[Math.max(0, i)];

    let titleText;
    let projectionText;
    let baselineText;
    if (yearsLater < 0) {
      titleText = `In ${schoolWithLineNodes.projections_years[0]}`;
      baselineText = `${format(schoolWithLineNodes.projections[0])} students`;
      const growth =
        schoolWithLineNodes.projections[0] - schoolWithLineNodes.projections[1];
      const lessOrMore = growth >= 0 ? 'more' : 'less';
      projectionText = `${Math.abs(
        growth
      )} ${lessOrMore} than ${schoolWithLineNodes.projections_years[1]}`;
    } else if (yearsLater === 0) {
      titleText = 'Baseline';
      baselineText = `${format(schoolWithLineNodes.projections[1])} students`;
      const growth =
        schoolWithLineNodes.projections[1] - schoolWithLineNodes.projections[0];
      const sign = growth >= 0 ? '+' : '';
      projectionText = `${sign}${growth} since ${schoolWithLineNodes
        .projections_years[0]}`;
    } else {  
      yearsLater -= 1
      if (isThisSchoolSmall && yearsLater > 0) { 
        titleText = ''; 
        projectionText = `${yearsLater} years later: <50`; 
      } else { 
        titleText = `${(inc < 0 ? '' : '+') + format(inc, 0)}% students`; 
        projectionText = `${yearsLater} years later: ${format(schoolWithLineNodes.projections[i], 0)}`; 
      } 
      baselineText = `Baseline: ${format(schoolWithLineNodes.projections[1])}`; 
    }

    tooltip.elm.querySelector('.tooltip-title').innerHTML = titleText;
    tooltip.elm.querySelector('.tooltip-baseline').innerHTML = baselineText;
    tooltip.elm.querySelector('.tooltip-projection').innerHTML = projectionText;

    const bb = this.getBoundingClientRect();
    const scrollX = window.scrollX || document.documentElement.scrollLeft;
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    const top =
      (bb.top + scrollY + (bb.top + scrollY)) / 2 -
      tooltip.elm.clientHeight -
      10;
    const left = bb.left + bb.width / 2 + scrollX - tooltip.elm.clientWidth / 2;
    tooltip.move(top, left);

    if (d3.event.type === 'mouseover') {
      clearTimeout(hideTimeout);
      d3.event.target.addEventListener('mouseout', () => {
        clearTimeout(hideTimeout);
        hideTimeout = setTimeout(() => {
          tooltip.hide();
        }, 2000);
      });
    }
  };
  setupToolTip();

  function getDateFromIndex(i) {
    const addYears = new Date(projectionStartDate.getTime());
    addYears.setYear(projectionStartDate.getFullYear() + i);
    return addYears;
  }

  d3.selection.prototype.moveToFront = function() {
    return this.each(function() {
      this.parentNode.appendChild(this);
    });
  };

  svg = d3
    .select(elm)
    .append('svg')
    .attr('viewBox', `0 0 ${SVGwidth} ${SVGheight}`);

  svg.attr('class', 'cluster-chart');

  const svgContainer = svg
    .append('g')
    .attr('class', 'stage')
    .attr('transform', `translate(${margin.left},${margin.top})`);

  // set the ranges
  var x = d3.scaleTime().range([0, width]);
  var y = d3.scaleLinear().range([height, 0]);

  // Scale the range of the data
  const maxX = d3.max(data, d =>
    d3.max(d.projections, (p, i) => getDateFromIndex(i))
  );
  const minX = projectionStartDate;
  x.domain([minX, maxX]);

  y.domain(getYDomain(data, activeSchool, isLimited));

  actualAndProjectedGrowth.drawRectangle(svgContainer, getDateFromIndex, x, y);

  const yAxis = svgContainer
    .append('g')
    .attr('class', 'yAxis')
    .attr('transform', `translate(-${domainPadding}, 0)`);

  // define the line
  const regularLine = d3
    .line()
    .x((d, i) => x(getDateFromIndex(i)))
    .y((d) => y(d));

  const schoolLine = d3
    .line()
    .x((d, i) => x(getDateFromIndex(i)))
    .y((d, i, e) => { 
      if (isSmallProjectedNode(i)) { 
        const currentYear = e[1]; 
        return y(currentYear); 
      } 
      return y(d); 
    });

  // Add the projectionsLine path.
  const schools = svgContainer
    .selectAll('.school')
    .data(data)
    .enter()
    .append('g')
    .attr('class', d => {
      let className = 'school';
      if (d.isActive) className += ' this-school';
      if (schoolWithLineNodes && d.uid === schoolWithLineNodes.uid)
        className += ' locally-active-school';
      return className;
    });

  schools
    .selectAll('path')
    .data(d => {
      return [d];
    })
    .enter()
    .append('path')
    .attr('class', 'line')
    .attr('fill', 'none')
    .style('stroke-dasharray', d => (d.isClusterAggregate ? '6, 3' : '3, 0'))
    .attr('d', d => {
      const isThisLineSmall = isSchoolSmall(d);
      if(isThisLineSmall) {
        return schoolLine(d.projections_percentage);
      }
      return regularLine(d.projections_percentage)
    });

  const legendKeyWrapper = d3
    .select(elm)
    .append('div')
    .attr('class', 'projections-key-legend');

  const keyWrapper = legendKeyWrapper
    .append('div')
    .attr('class', 'projections-key');

  actualAndProjectedGrowth.drawLabels(svgContainer, getDateFromIndex, x, y);

  enrolmentPercentLabel.draw(svgContainer, domainPadding, y);

  function updateChart() {
    drawKey(keyWrapper, activeCluster, activeSchool, schoolWithLineNodes);
    enrolmentPercentLabel.update();
    svgContainer
      .selectAll('.school path')
      .attr('stroke', getSchoolColor(schoolWithLineNodes))
      .attr('stroke-width', d => {
        if (d.isActive) return 3;
        if (d.isClusterAggregate) return 3;
        if (d.uid === schoolWithLineNodes.uid) return 3;
        return 1;
      });

    const nodesData = schoolWithLineNodes.projections_percentage;

    actualAndProjectedGrowth.update(svgContainer, getDateFromIndex, x, y);
    let multiplier = 2.5;
    let ticks = 5;
    if (window.matchMedia('(min-width: 600px)').matches) {
      multiplier = 1;
      ticks = null;
    }

    yAxis.call(
      d3.axisLeft(y).ticks(ticks).tickFormat(d => {
        // Don't show the top most axis tick - the y-axis label would cover it.
        if (d === y.domain()[1]) return '';
        // Don't show ticks below 0%. It should almost never happen that a
        // cluster goes down in enrolments. So negative values would only come
        // into play for the "actual growth" segment of the line, which DoE
        // prefers to indicate in actual number of extra students, not percent
        if (d < 0) return '';
        return d;
      })
    );

    const customAxis = getCustomAxis(width, domainPadding);
    yAxis.call(customAxis);

    schools.attr('class', d => {
      let className = 'school';
      if (d.isActive) className += ' this-school';
      if (schoolWithLineNodes && d.uid === schoolWithLineNodes.uid) {
        className += ' locally-active-school';
      }
      return className;
    });

    svg.select('.circle-group').remove();
    const nodesGroup = svg
      .select('.locally-active-school')
      .append('g')
      .attr('class', 'circle-group');

    svg.select('.locally-active-school').moveToFront();


    const isLocallyActiveSchoolSmall = isSchoolSmall(schoolWithLineNodes)

    addNodes(
      nodesGroup,
      nodesData,
      schoolWithLineNodes,
      getDateFromIndex,
      handleLineNodeClick,
      multiplier,
      x,
      y,
      isLocallyActiveSchoolSmall
    );

    addXAxisLabels(
      nodesGroup,
      nodesData,
      schoolWithLineNodes,
      getDateFromIndex,
      x,
      height,
      axisPadding
    );
  }

  window.addEventListener('resize', updateChart);
  updateChart();

  return {
    updateChart: updateChart,
    elm: elm,
  };
}

class ClusterProjections extends Component {
  wrapChart() {
    this.container = findDOMNode(this);
    while (this.container.firstChild) {
      this.container.removeChild(this.container.firstChild);
    }
    const { data, school } = this.props;
    this.chart = makeProjectionsLineGraph(school, data);
    this.container.appendChild(this.chart.elm);
  }

  componentDidMount() {
    this.wrapChart();
  }

  componentDidUpdate() {
    if (d3.event) d3.event.stopPropagation();
    schoolWithLineNodes = this.props.ui.selectedSchool
      ? this.props.ui.selectedSchool
      : null;
    if (schoolWithLineNodes) {
      this.chart.updateChart();
    }
  }

  render() {
    return <div />;
  }
}

const mapStateToProps = (state, ownProps) => {
  return { ui: state.ui[ownProps.chartName] };
};

const mapDispatchToProps = dispatch => ({
  selectSchool: p => dispatch(selectSchool(p)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ClusterProjections);



// WEBPACK FOOTER //
// ./src/components/singleClusterProjectionsLineGraph/index.js